import { canonicalSha256 } from '@sqi/domain';
import { z } from 'zod';
const pendingSchema = z
  .object({ key: z.uuid(), hash: z.string().regex(/^[0-9a-f]{64}$/) })
  .strict();
export interface OperationScope {
  actorId: string;
  organizationId: string;
  projectId: string;
  action: string;
}
function slot(scope: OperationScope) {
  return 'sqi-ops-pending-v1:' + JSON.stringify(scope);
}
/** Store only an opaque mutation identifier and body digest; no supplier, row, evidence or credential payload.
 * Session storage survives reload but never queues a transition for offline execution. */
export async function retainOperationsMutation(
  store: Storage,
  scope: OperationScope,
  payload: unknown,
) {
  const hash = await canonicalSha256(payload);
  const saved = store.getItem(slot(scope));
  if (saved) {
    const parsed = pendingSchema.safeParse(JSON.parse(saved) as unknown);
    if (!parsed.success) throw new Error('pending_state_invalid');
    if (parsed.data.hash !== hash) throw new Error('pending_body_conflict');
    return parsed.data.key;
  }
  const key = crypto.randomUUID();
  store.setItem(slot(scope), JSON.stringify({ key, hash }));
  return key;
}
export function pendingOperationsMutation(store: Storage, scope: OperationScope) {
  const saved = store.getItem(slot(scope));
  return saved ? pendingSchema.parse(JSON.parse(saved) as unknown) : null;
}
export function settleOperationsMutation(store: Storage, scope: OperationScope, key: string) {
  const pending = pendingOperationsMutation(store, scope);
  if (pending?.key === key) store.removeItem(slot(scope));
}
export function scopedPendingOperations(store: Storage, scope: Omit<OperationScope, 'action'>) {
  const rows: { scope: OperationScope; key: string }[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (!key?.startsWith('sqi-ops-pending-v1:')) continue;
    try {
      const saved = z
        .object({
          actorId: z.uuid(),
          organizationId: z.uuid(),
          projectId: z.uuid(),
          action: z.string().min(1).max(100),
        })
        .strict()
        .parse(JSON.parse(key.slice('sqi-ops-pending-v1:'.length)) as unknown);
      if (
        saved.actorId === scope.actorId &&
        saved.organizationId === scope.organizationId &&
        saved.projectId === scope.projectId
      ) {
        const pending = pendingOperationsMutation(store, saved);
        if (pending) rows.push({ scope: saved, key: pending.key });
      }
    } catch {
      /* Invalid or unrelated metadata never becomes a request. */
    }
  }
  return rows;
}
